import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft, ExternalLink, Copy, Check,
  Lock, Eye, EyeOff, RefreshCw, Factory,
} from "lucide-react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { OrderAuditor } from "@/components/OrderAuditor";
import { PortalAutoFill } from "@/components/PortalAutoFill";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Pull the current MTM order draft from localStorage and format it into a
// flat list of "label → value" rows so the salesperson can copy each field
// straight into the factory portal form. Keys mirror the same localStorage
// shape used across the MTM POS flow.
function buildOrderSnapshot() {
  const safe = (k) => {
    try {
      const v = localStorage.getItem(k);
      return v ? JSON.parse(v) : null;
    } catch {
      return null;
    }
  };
  const general = safe("mtm_order") || safe("mtm_general") || {};
  const fabric = safe("mtm_fabric") || {};
  const measurements = safe("mtm_measurements") || {};
  const styles = safe("mtm_styles") || {};

  const fabricCode =
    fabric.fabricSelection?.rc_code ||
    fabric.fabricSelection?.code ||
    "";
  const liningCode = fabric.liningSelection?.rc_code || "";
  const pipingCode = fabric.pipingSelection?.rc_code || fabric.piping || "";

  const rows = [
    { group: "Customer", label: "Name", value: general.customerName || general.client_name || general.customer_name || "" },
    { group: "Customer", label: "Phone", value: general.customerPhone || general.phone || "" },
    { group: "Customer", label: "Email", value: general.customerEmail || general.email || "" },

    { group: "Garment", label: "Garment Type", value: general.garmentType || general.garment_type || "" },
    { group: "Garment", label: "Construction", value: fabric.constructionType || "" },
    { group: "Garment", label: "Lining Type", value: fabric.liningType || "" },

    { group: "Fabric", label: "Fabric Code", value: fabricCode },
    { group: "Fabric", label: "Body Lining", value: liningCode },
    { group: "Fabric", label: "Piping", value: pipingCode },
    { group: "Fabric", label: "Jacket Button", value: fabric.jacketButtonSelection?.code || "" },
    { group: "Fabric", label: "Vest Button", value: fabric.vestButtonSelection?.code || "" },
    { group: "Fabric", label: "Shirt Button", value: fabric.shirtButtonSelection?.code || "" },
  ];

  // Measurements — flatten the most-used keys in a sensible order
  const m = measurements?.values || measurements || {};
  const measurementOrder = [
    "Chest", "Stomach", "Seat", "Shoulders", "Neck",
    "Sleeve", "Sleeve Length", "Back Length", "Front Length",
    "Waist", "Hip", "Thigh", "Knee", "Bottom", "Inseam", "Outseam",
    "Cuff", "Yoke", "Bicep",
  ];
  measurementOrder.forEach((label) => {
    const key = Object.keys(m).find((k) => k.toLowerCase() === label.toLowerCase());
    if (key && m[key]?.final !== undefined && m[key].final !== "") {
      rows.push({ group: "Measurements", label, value: String(m[key].final) });
    }
  });

  // Selected styles — pull plain string values
  if (styles && typeof styles === "object") {
    Object.entries(styles).forEach(([k, v]) => {
      if (typeof v === "string" && v) {
        rows.push({ group: "Styles", label: k, value: v });
      }
    });
  }

  return rows.filter((r) => r.value !== "" && r.value !== null && r.value !== undefined);
}

const FactoryPortalPage = ({ portalKey }) => {
  const navigate = useNavigate();
  const [portal, setPortal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreds, setShowCreds] = useState(false);
  const [copiedKey, setCopiedKey] = useState(null);
  const [helperOpen, setHelperOpen] = useState(true);

  useEffect(() => {
    const adminAuth = sessionStorage.getItem("adminAuth");
    if (adminAuth !== "true") {
      navigate("/admin");
      return;
    }
    fetchPortal();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [portalKey]);

  const fetchPortal = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/factory-portals/${portalKey}`);
      setPortal(res.data);
    } catch (e) {
      toast.error("Failed to load portal configuration");
    } finally {
      setLoading(false);
    }
  };

  const orderRows = useMemo(() => buildOrderSnapshot(), []);
  const groupedOrder = useMemo(() => {
    const groups = {};
    orderRows.forEach((r) => {
      (groups[r.group] = groups[r.group] || []).push(r);
    });
    return groups;
  }, [orderRows]);

  const copy = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedKey(key);
      toast.success(`Copied: ${text}`);
      setTimeout(() => setCopiedKey(null), 1500);
    } catch {
      toast.error("Copy failed");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <RefreshCw size={20} className="animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20">
      {/* Header */}
      <section className="border-b border-white/5 bg-card/30">
        <div className="max-w-[1800px] mx-auto px-4 md:px-6 py-3 flex flex-wrap items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/admin")}
            data-testid="portal-back-btn"
          >
            <ArrowLeft size={14} className="mr-1" /> Admin
          </Button>
          <div className="flex items-center gap-2 mr-auto">
            <Factory size={18} className="text-amber-400" />
            <div>
              <h1 className="text-base font-medium leading-tight">{portal.name}</h1>
              <p className="text-[11px] text-muted-foreground leading-tight">{portal.url}</p>
            </div>
          </div>

          {/* Credentials quick-copy */}
          <div className="flex items-center gap-2 text-xs">
            <span className="text-muted-foreground hidden md:inline">Login:</span>
            <code className="bg-muted/40 px-2 py-1 rounded font-mono">{portal.username || "—"}</code>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => copy(portal.username, "username")}
              disabled={!portal.username}
              data-testid="portal-copy-username"
            >
              {copiedKey === "username" ? <Check size={12} /> : <Copy size={12} />}
            </Button>
            <code className="bg-muted/40 px-2 py-1 rounded font-mono">
              {showCreds ? portal.password || "—" : "••••••••"}
            </code>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowCreds((v) => !v)}
              data-testid="portal-toggle-password"
            >
              {showCreds ? <EyeOff size={12} /> : <Eye size={12} />}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => copy(portal.password, "password")}
              disabled={!portal.password}
              data-testid="portal-copy-password"
            >
              {copiedKey === "password" ? <Check size={12} /> : <Copy size={12} />}
            </Button>
          </div>

          <Button
            variant="outline"
            size="sm"
            asChild
            data-testid="portal-open-new-tab"
          >
            <a href={portal.url} target="_blank" rel="noreferrer">
              <ExternalLink size={12} className="mr-1" /> New tab
            </a>
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/admin/settings")}
            data-testid="portal-edit-creds"
          >
            <Lock size={12} className="mr-1" /> Edit
          </Button>
        </div>
      </section>

      {/* Main */}
      <div className="max-w-[1800px] mx-auto px-2 md:px-4 py-4">
        <div className="flex gap-3" style={{ height: "calc(100vh - 160px)" }}>
          {/* Open portal hero panel (replaces the iframe — login can't work in iframe
              because of cross-site cookie + captcha restrictions) */}
          <div className="flex-1 bg-card border border-white/10 rounded-lg overflow-hidden flex items-center justify-center p-8">
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center max-w-lg"
            >
              <div className="w-14 h-14 rounded-full bg-amber-500/10 flex items-center justify-center mx-auto mb-4">
                <Factory size={26} className="text-amber-400" />
              </div>
              <h2 className="text-xl font-medium mb-2">{portal.name}</h2>
              <p className="text-sm text-muted-foreground mb-4">
                Login must happen in a real browser tab — modern browsers block
                third-party cookies and captchas inside embedded views.
              </p>

              {/* Credentials reminder */}
              {(portal.username || portal.password) && (
                <div className="bg-muted/20 border border-white/10 rounded-md p-3 mb-4 text-left">
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2">
                    Your saved login (click to copy)
                  </p>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <button
                      onClick={() => copy(portal.username, "hero-username")}
                      className="flex items-center justify-between bg-muted/30 hover:bg-amber-500/10 border border-white/5 rounded px-2 py-1.5 transition-colors"
                      data-testid="hero-copy-username"
                    >
                      <div className="text-left">
                        <div className="text-[10px] text-muted-foreground">Username</div>
                        <div className="font-mono truncate">{portal.username || "—"}</div>
                      </div>
                      {copiedKey === "hero-username" ? (
                        <Check size={12} className="text-emerald-400 shrink-0" />
                      ) : (
                        <Copy size={12} className="text-muted-foreground shrink-0" />
                      )}
                    </button>
                    <button
                      onClick={() => copy(portal.password, "hero-password")}
                      className="flex items-center justify-between bg-muted/30 hover:bg-amber-500/10 border border-white/5 rounded px-2 py-1.5 transition-colors"
                      data-testid="hero-copy-password"
                    >
                      <div className="text-left min-w-0">
                        <div className="text-[10px] text-muted-foreground">Password</div>
                        <div className="font-mono truncate">
                          {showCreds ? portal.password || "—" : "••••••••"}
                        </div>
                      </div>
                      {copiedKey === "hero-password" ? (
                        <Check size={12} className="text-emerald-400 shrink-0" />
                      ) : (
                        <Copy size={12} className="text-muted-foreground shrink-0" />
                      )}
                    </button>
                  </div>
                </div>
              )}

              <Button
                size="lg"
                asChild
                className="bg-amber-500 hover:bg-amber-600 text-black"
                data-testid="hero-open-portal-btn"
              >
                <a href={portal.url} target="_blank" rel="noreferrer">
                  <ExternalLink size={16} className="mr-2" /> Open {portal.name}
                </a>
              </Button>

              <p className="text-[11px] text-muted-foreground mt-4">
                Once logged in, keep that tab open and use the panel on the right
                to copy each field. {portalKey === "rcmtm" && (
                  <>For RCMTM, you can also use the <strong>Auto-Fill</strong>{" "}
                  bookmarklet on the right to type codes straight into the form.</>
                )}
              </p>
            </motion.div>
          </div>

          {/* Order Submission Helper */}
          <div
            className={`bg-card border border-white/10 rounded-lg overflow-hidden flex flex-col transition-all ${
              helperOpen ? "w-[340px]" : "w-[40px]"
            }`}
          >
            <div className="p-3 border-b border-white/5 flex items-center justify-between">
              {helperOpen && (
                <div>
                  <h3 className="text-sm font-medium">Order Helper</h3>
                  <p className="text-[10px] text-muted-foreground">
                    {orderRows.length} fields ready • click to copy
                  </p>
                </div>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setHelperOpen((v) => !v)}
                data-testid="order-helper-toggle"
              >
                {helperOpen ? "›" : "‹"}
              </Button>
            </div>
            {helperOpen && (
              <div className="flex-1 overflow-y-auto p-3 space-y-4">
                {/* Auto-fill into RCMTM portal */}
                {portalKey === "rcmtm" && <PortalAutoFill portalUrl={portal.url} />}

                {/* Order QA — auto-validate before factory submission */}
                <OrderAuditor compact />

                {orderRows.length === 0 && (
                  <p className="text-xs text-muted-foreground">
                    No active MTM draft. Start an order at <a className="text-primary" href="/admin/mtm-order">MTM Order</a> and the
                    fields will show up here.
                  </p>
                )}
                {Object.entries(groupedOrder).map(([group, items]) => (
                  <div key={group}>
                    <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">{group}</p>
                    <div className="space-y-1">
                      {items.map((r, idx) => (
                        <button
                          key={`${group}-${r.label}-${idx}`}
                          onClick={() => copy(String(r.value), `${group}-${r.label}`)}
                          className="w-full text-left bg-muted/30 hover:bg-amber-500/10 border border-white/5 rounded px-2 py-1.5 text-xs flex items-center justify-between gap-2 group transition-colors"
                          data-testid={`helper-copy-${group.toLowerCase()}-${r.label.replace(/\s+/g, "-").toLowerCase()}`}
                        >
                          <div className="min-w-0 flex-1">
                            <div className="text-[10px] text-muted-foreground">{r.label}</div>
                            <div className="font-mono truncate">{r.value}</div>
                          </div>
                          {copiedKey === `${group}-${r.label}` ? (
                            <Check size={12} className="text-emerald-400 shrink-0" />
                          ) : (
                            <Copy size={12} className="text-muted-foreground group-hover:text-amber-400 shrink-0" />
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FactoryPortalPage;
